import { AxiosError } from "axios";
import { ApiErrorEnvelope, ApiErrorPayload, isApiErrorEnvelope } from "../logic/ApiEnvelope";
import i18n from "../i18n";

export type ApiErrorCode =
  | "VALIDATION_ERROR"
  | "BAD_REQUEST"
  | "UNAUTHORIZED"
  | "FORBIDDEN"
  | "NOT_FOUND"
  | "CONFLICT"
  | "RATE_LIMIT_EXCEEDED"
  | "INTERNAL_SERVER_ERROR"
  | "NETWORK_ERROR"
  | "UNKNOWN";

export class ApiError extends Error {
  public readonly code: ApiErrorCode;
  public readonly statusCode: number;
  public readonly fieldErrors: Record<string, string>;
  /**
   * The backend's `X-Request-Id` for this failed request, when present.
   * The server now exposes it via CORS (finding M3); surfacing it on
   * the error lets us log a single token users can quote in support
   * tickets to pinpoint the exact server-side request.
   */
  public readonly requestId: string | undefined;

  constructor(
    message: string,
    code: ApiErrorCode,
    statusCode: number,
    fieldErrors: Record<string, string> = {},
    requestId?: string,
  ) {
    super(message);
    this.name = "ApiError";
    this.code = code;
    this.statusCode = statusCode;
    this.fieldErrors = fieldErrors;
    this.requestId = requestId;
  }
}

/**
 * Pull the `X-Request-Id` off an axios response's headers, regardless of
 * whether they arrive as a plain object or an `AxiosHeaders` instance.
 * Header names are case-insensitive; axios lower-cases them on the way in.
 */
function extractRequestId(thrown: AxiosError): string | undefined {
  const headers = thrown.response?.headers;
  if (!headers) return undefined;
  const value =
    typeof (headers as { get?: (name: string) => unknown }).get === "function"
      ? (headers as { get: (name: string) => unknown }).get("x-request-id")
      : (headers as Record<string, unknown>)["x-request-id"];
  return typeof value === "string" && value.length > 0 ? value : undefined;
}

/**
 * Localized friendly fallback for an error code, resolved through the
 * i18n instance (`errors.api.*`). This is a non-React util, so it reads
 * the instance directly rather than via the `useTranslation` hook.
 */
function friendlyMessage(code: ApiErrorCode): string {
  return i18n.t(`api.${code}`, { ns: "errors" });
}

function statusToCode(status: number): ApiErrorCode {
  if (status === 400) return "BAD_REQUEST";
  if (status === 401) return "UNAUTHORIZED";
  if (status === 403) return "FORBIDDEN";
  if (status === 404) return "NOT_FOUND";
  if (status === 409) return "CONFLICT";
  if (status === 422) return "VALIDATION_ERROR";
  if (status === 429) return "RATE_LIMIT_EXCEEDED";
  if (status >= 500) return "INTERNAL_SERVER_ERROR";
  return "UNKNOWN";
}

function fieldErrorsFromDetails(details: ApiErrorPayload["details"]): Record<string, string> {
  const result: Record<string, string> = {};
  if (!details) return result;
  for (const detail of details) {
    if (detail && typeof detail.field === "string" && detail.field.length > 0) {
      // First message wins if multiple per field.
      if (!(detail.field in result)) {
        result[detail.field] = detail.message ?? "";
      }
    }
  }
  return result;
}

/**
 * Normalizes any thrown value into an ApiError. Caller can rely on
 * `.message` for user-facing copy and `.fieldErrors` for form-level
 * display. The original error is preserved on `.cause` for diagnostics.
 */
export function parseApiError(thrown: unknown): ApiError {
  if (thrown instanceof ApiError) {
    return thrown;
  }

  if (thrown instanceof AxiosError) {
    const status = thrown.response?.status ?? 0;
    const body = thrown.response?.data;
    const requestId = extractRequestId(thrown);

    if (status === 0 || thrown.code === "ERR_NETWORK") {
      return new ApiError(friendlyMessage("NETWORK_ERROR"), "NETWORK_ERROR", 0);
    }

    if (isApiErrorEnvelope(body)) {
      const envelope: ApiErrorEnvelope = body;
      const code = (envelope.error.code as ApiErrorCode) ?? statusToCode(status);
      const fieldErrors = fieldErrorsFromDetails(envelope.error.details);
      const message =
        envelope.message || friendlyMessage(code as ApiErrorCode) || friendlyMessage("UNKNOWN");
      return new ApiError(message, code, status, fieldErrors, requestId);
    }

    // Backend wrapped failure with no `error` block, or an unexpected
    // error shape — fall back to status-based mapping.
    const code = statusToCode(status);
    const messageFromBody =
      body && typeof body === "object" && "message" in body
        ? String((body as { message: unknown }).message)
        : null;
    const message = messageFromBody || thrown.message || friendlyMessage(code);
    return new ApiError(message, code, status, {}, requestId);
  }

  if (thrown instanceof Error) {
    return new ApiError(thrown.message, "UNKNOWN", 0);
  }

  return new ApiError(friendlyMessage("UNKNOWN"), "UNKNOWN", 0);
}
